import React, { MouseEvent, TouchEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { icon_chevron_left, icon_chevron_right } from '@gn-audio/icons';
import { JabraIcon, useFallbackTranslation } from '@gn-audio/jss-components';

import TabNavigationItem from './TabNavigationItem';
import { TabNavigationItemFields } from './tabNavigationSpotTypes';

type ImageTabSliderProps = {
  tabs?: TabNavigationItemFields[];
  activeIndex: number | null;
  handleTabClick: (index: number) => void;
  instanceId?: number;
};

const ImageTabSlider: React.FC<ImageTabSliderProps> = ({
  tabs,
  activeIndex,
  handleTabClick,
  instanceId,
}) => {
  const { t } = useFallbackTranslation();
  const [currentSlide, setCurrentSlide] = useState(activeIndex || 0);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);

  const sliderRef = useRef<HTMLDivElement>(null);
  const slidesContainerRef = useRef<HTMLDivElement>(null);

  const tabsCount = tabs?.length || 0;

  useEffect(() => {
    if (activeIndex !== null) {
      setCurrentSlide(activeIndex);
      moveToSlide(activeIndex);
    }
  }, [activeIndex]);

  const moveToSlide = (index: number) => {
    if (!slidesContainerRef.current) return;

    const slideWidth = slidesContainerRef.current.offsetWidth;
    const newTranslate = index * -slideWidth;

    setCurrentTranslate(newTranslate);
    setPrevTranslate(newTranslate);

    slidesContainerRef.current.style.transform = `translateX(${newTranslate}px)`;
  };

  const nextSlide = () => {
    if (currentSlide < tabsCount - 1) {
      handleTabClick(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      handleTabClick(currentSlide - 1);
    }
  };

  const handleDragStart = (e: MouseEvent | TouchEvent) => {
    setIsDragging(true);

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartPosition(clientX);
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !slidesContainerRef.current) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = clientX - startPosition;

    const newTranslate = prevTranslate + diff;
    setCurrentTranslate(newTranslate);

    slidesContainerRef.current.style.transform = `translateX(${newTranslate}px)`;
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const slideWidth = slidesContainerRef.current?.offsetWidth || 0;
    const threshold = slideWidth * 0.2;
    const moveDistance = currentTranslate - prevTranslate;

    if (moveDistance < -threshold && currentSlide < tabsCount - 1) {
      handleTabClick(currentSlide + 1);
    } else if (moveDistance > threshold && currentSlide > 0) {
      handleTabClick(currentSlide - 1);
    } else {
      moveToSlide(currentSlide);
    }
  };

  return (
    <div
      ref={sliderRef}
      className="relative rounded-[37px] bg-[var(--tab-bg-color)] p-2 text-[var(--tab-text-color)]"
    >
      <button
        onClick={prevSlide}
        disabled={currentSlide === 0}
        className={classNames(
          'absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-2',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-0'
        )}
        aria-label={t('system.Previous')}
        tabIndex={currentSlide === 0 ? -1 : undefined}
      >
        <JabraIcon Icon={icon_chevron_left} className="h-5 w-5 text-[var(--tab-text-color)]" />
      </button>

      <div className="overflow-hidden px-10">
        <div
          ref={slidesContainerRef}
          className={classNames(
            'flex transition-transform duration-300 ease-out',
            isDragging ? 'cursor-grabbing transition-none' : 'cursor-grab'
          )}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {tabs?.map((tab, index) => (
            <div
              key={`mobile-tab-${tab.id}`}
              className={classNames(
                'flex w-full flex-shrink-0 justify-center',
                index !== currentSlide ? 'pointer-events-none opacity-0' : 'opacity-100'
              )}
              onClick={() => !isDragging && handleTabClick(index)}
              tabIndex={index !== currentSlide ? -1 : undefined}
              aria-hidden={index !== currentSlide}
            >
              <TabNavigationItem
                tab={tab}
                index={index}
                navigationType="image"
                activeIndex={activeIndex}
                handleTabClick={handleTabClick}
                instanceId={instanceId}
              />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={nextSlide}
        disabled={currentSlide === tabsCount - 1}
        className={classNames(
          'absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-2',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-0'
        )}
        aria-label={t('system.Next')}
        tabIndex={currentSlide === tabsCount - 1 ? -1 : undefined}
      >
        <JabraIcon Icon={icon_chevron_right} className="h-5 w-5 text-[var(--tab-text-color)]" />
      </button>
    </div>
  );
};

export default ImageTabSlider;
